import { assertStaffCapability } from "./authorization.js";
import { boundedText } from "./bounded-text.js";
import { catalogPriceSources, catalogPriceUnits, catalogPricingModels, } from "./catalog.js";
import { DomainValidationError } from "./identifiers.js";
import { minorUnits, money, supportedCurrency, } from "./money.js";
import { checkExpectedVersion, isMember, requireReason, violation } from "./transition-support.js";
// The database CHECK `catalog_items_pricing_shape` says the same, model by model. Validity (a window
// or the no-expiry flag) is a property of a price, so only the priced models may carry it; the
// database leaves that one rule to this code (`validity`).
const SHAPES = {
    UNKNOWN: {
        amount: false,
        min: false,
        max: false,
        currency: false,
        unit: "none",
        validity: false,
    },
    QUOTE_REQUIRED: {
        amount: false,
        min: false,
        max: false,
        currency: false,
        unit: "none",
        validity: false,
    },
    INFORMATION_ONLY: {
        amount: true,
        min: false,
        max: false,
        currency: true,
        unit: "optional",
        validity: true,
    },
    FIXED: { amount: true, min: false, max: false, currency: true, unit: "required", validity: true },
    FROM: { amount: false, min: true, max: false, currency: true, unit: "required", validity: true },
    RANGE: { amount: false, min: true, max: true, currency: true, unit: "required", validity: true },
};
const fail = (field, rule) => new DomainValidationError(field, rule);
const isValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());
/** An amount in minor units: a safe non-negative integer, or absent. */
function amountOf(value) {
    return value === null ? null : minorUnits(value);
}
/**
 * Validates a price: the model decides which amounts, currency and unit may be set (the same table
 * the database CHECK enforces), the currency is on the allow-list, the amounts are safe
 * non-negative integers in minor units, and the validity window is ordered. The result is what the
 * adapter writes. Validating never grants payment authority: that is `planPriceAuthorization`.
 */
export function validateCatalogPricing(input) {
    if (!isMember(catalogPricingModels, input.model))
        throw fail("model", "must be a known pricing model");
    const shape = SHAPES[input.model];
    const amountMinor = amountOf(input.amountMinor);
    const minAmountMinor = amountOf(input.minAmountMinor);
    const maxAmountMinor = amountOf(input.maxAmountMinor);
    const currency = input.currency === null ? null : supportedCurrency(input.currency);
    const unit = input.unit;
    if (unit !== null && !isMember(catalogPriceUnits, unit))
        throw fail("unit", "must be a known price unit");
    const source = input.source;
    if (source !== null && !isMember(catalogPriceSources, source)) {
        throw fail("source", "must be a known price source");
    }
    if ((amountMinor !== null) !== shape.amount) {
        throw fail("amountMinor", `does not fit the ${input.model} model`);
    }
    if ((minAmountMinor !== null) !== shape.min) {
        throw fail("minAmountMinor", `does not fit the ${input.model} model`);
    }
    if ((maxAmountMinor !== null) !== shape.max) {
        throw fail("maxAmountMinor", `does not fit the ${input.model} model`);
    }
    if ((currency !== null) !== shape.currency) {
        throw fail("currency", `does not fit the ${input.model} model`);
    }
    if ((shape.unit === "none" && unit !== null) || (shape.unit === "required" && unit === null)) {
        throw fail("unit", `does not fit the ${input.model} model`);
    }
    if (minAmountMinor !== null && maxAmountMinor !== null && minAmountMinor > maxAmountMinor) {
        throw fail("minAmountMinor", "must not be above the maximum");
    }
    if (input.validFrom !== null && !isValidDate(input.validFrom))
        throw fail("validFrom", "must be a valid date");
    if (input.validUntil !== null && !isValidDate(input.validUntil))
        throw fail("validUntil", "must be a valid date");
    if (input.validFrom !== null &&
        input.validUntil !== null &&
        input.validUntil.getTime() <= input.validFrom.getTime()) {
        throw fail("validUntil", "must be after the start of validity");
    }
    if (input.noExpiry && input.validUntil !== null) {
        throw fail("noExpiry", "cannot be set together with an end date");
    }
    if (!shape.validity &&
        (input.validFrom !== null || input.validUntil !== null || input.noExpiry)) {
        throw fail("validity", `does not fit the ${input.model} model`);
    }
    return Object.freeze({
        model: input.model,
        currency,
        amountMinor,
        minAmountMinor,
        maxAmountMinor,
        unit,
        terms: input.terms === null ? null : boundedText("terms", input.terms, 500, { multiline: true }),
        source,
        validFrom: input.validFrom,
        validUntil: input.validUntil,
        noExpiry: input.noExpiry,
    });
}
/**
 * An editor's price change. It always de-authorizes in the same patch: an edit can never keep a
 * stale approval. `wasAuthoritative` tells the audit row what was cleared. Only a catalog editor
 * edits (`catalog.edit`); an archived item is not editable.
 */
export function planPricingUpdate(input) {
    assertStaffCapability(input.actor, "catalog.edit", input.environment);
    if (input.item.status === "ARCHIVED")
        throw violation("catalog.archived");
    checkExpectedVersion(input.actor, input.expectedVersion, input.item.version, "catalog.version_conflict");
    const pricing = validateCatalogPricing(input.pricing);
    return Object.freeze({
        patch: {
            kind: "update_pricing",
            pricing,
            authoritative: false,
            authorizedByStaffUserId: null,
            authorizedAt: null,
        },
        wasAuthoritative: input.item.authoritative,
        auditAction: "catalog_item.pricing_updated",
    });
}
/**
 * Marks a FIXED price payment-grade (owner decision O1): a catalog editor or a supervisor, with a
 * reason, and only for a price that has an amount, an allowed currency and a bounded validity (an
 * end date in the future, or the explicit no-expiry flag). Price edit and price approval can be one
 * role; the audit trail (actor, reason, correlation id) is the control, and the next price edit
 * clears the mark again. Only a `HUMAN_CONCIERGE` item can be marked.
 */
export function planPriceAuthorization(input) {
    const { snapshot, actor } = input;
    const staff = assertStaffCapability(actor, "catalog.authorize_price", input.environment);
    if (snapshot.status === "ARCHIVED")
        throw violation("catalog.archived");
    checkExpectedVersion(actor, input.expectedVersion, snapshot.catalogItemVersion, "catalog.version_conflict");
    requireReason(input.reason);
    if (snapshot.model !== "FIXED")
        throw violation("catalog.price_not_fixed");
    // The database CHECK `catalog_items_price_authority_fulfilment` says the same: Lafayette charges
    // only for what its concierge arranges (owner decision Q1, CHT-705).
    if (input.fulfilmentMode !== "HUMAN_CONCIERGE") {
        throw violation("catalog.price_fulfilment_not_payable");
    }
    if (snapshot.amountMinor === null)
        throw fail("amountMinor", "is required to authorize a price");
    // The database CHECK `catalog_items_price_authority_shape` says the same: a free item is not payable.
    if (snapshot.amountMinor <= 0)
        throw violation("catalog.price_not_payable");
    supportedCurrency(snapshot.currency);
    if (snapshot.validUntil === null) {
        if (!snapshot.noExpiry) {
            throw fail("validity", "needs an end date or the explicit no-expiry flag");
        }
    }
    else if (snapshot.validUntil.getTime() <= input.now.getTime()) {
        throw fail("validity", "must end in the future");
    }
    return Object.freeze({
        patch: {
            kind: "authorize_price",
            authoritative: true,
            authorizedByStaffUserId: staff.authority.staffUserId,
            authorizedAt: "now",
        },
        auditAction: "catalog_item.price_authorized",
    });
}
/**
 * Server-side price resolution for a direct catalog payment request. A price is eligible only when
 * the item is PUBLISHED, the model is FIXED, a catalog editor or supervisor marked it payment-grade
 * (and bounded its validity), the amount is there and above zero, the unit is PER_BOOKING (the price
 * is then the whole charge; any other unit needs a quantity that nothing computes yet, so it is not
 * eligible: `unit_requires_quantity`), `now` is inside the validity window, and the currency is on
 * the caller's allow-list (Phase 2.5 passes INR only). It takes no amount from a
 * caller: the snapshot comes from Postgres, and the caller copies the amount, currency, item id,
 * item version and authorization time into the payment request's source trace. Checks run in this
 * order, so the code names the first thing that is wrong.
 */
export function resolveEligibleFixedPrice(snapshot, now, allowedCurrencies) {
    if (snapshot.status !== "PUBLISHED")
        return { eligible: false, code: "not_published" };
    if (snapshot.model !== "FIXED")
        return { eligible: false, code: "not_fixed" };
    if (!snapshot.authoritative ||
        snapshot.authorizedAt === null ||
        // An approval with no end date and no explicit no-expiry flag is not an approval.
        (snapshot.validUntil === null && !snapshot.noExpiry)) {
        return { eligible: false, code: "not_authoritative" };
    }
    if (snapshot.amountMinor === null)
        return { eligible: false, code: "amount_missing" };
    if (snapshot.amountMinor <= 0)
        return { eligible: false, code: "amount_not_payable" };
    if (snapshot.unit !== "PER_BOOKING")
        return { eligible: false, code: "unit_requires_quantity" };
    if (snapshot.validFrom !== null && now.getTime() < snapshot.validFrom.getTime()) {
        return { eligible: false, code: "not_yet_valid" };
    }
    if (snapshot.validUntil !== null && now.getTime() >= snapshot.validUntil.getTime()) {
        return { eligible: false, code: "expired" };
    }
    if (snapshot.currency === null ||
        !allowedCurrencies.includes(snapshot.currency)) {
        return { eligible: false, code: "currency_not_allowed" };
    }
    return {
        eligible: true,
        price: money(snapshot.amountMinor, snapshot.currency),
        // FIXED always has a unit (the CHECK says so); a snapshot without one cannot come from the database.
        unit: snapshot.unit,
        catalogItemId: snapshot.catalogItemId,
        catalogItemVersion: snapshot.catalogItemVersion,
        authorizedAt: snapshot.authorizedAt,
    };
}
// --- Price class (staff view) -----------------------------------------------------------------------
export const catalogPriceClasses = Object.freeze([
    "PAYMENT_GRADE",
    "FIXED_NOT_PAYABLE",
    "INDICATIVE",
    "QUOTE",
    "UNKNOWN",
]);
/**
 * Classifies a price. `PAYMENT_GRADE` is `resolveEligibleFixedPrice(...).eligible`, the same rule
 * that Phase 2.5 payment code uses, never a copy of it; `allowedCurrencies` is the caller's payment
 * allow-list.
 */
export function classifyCatalogPrice(snapshot, now, allowedCurrencies) {
    switch (snapshot.model) {
        case "FIXED":
            return resolveEligibleFixedPrice(snapshot, now, allowedCurrencies).eligible
                ? "PAYMENT_GRADE"
                : "FIXED_NOT_PAYABLE";
        case "INFORMATION_ONLY":
        case "FROM":
        case "RANGE":
            return "INDICATIVE";
        case "QUOTE_REQUIRED":
            return "QUOTE";
        case "UNKNOWN":
            return "UNKNOWN";
    }
}
